import React from 'react';
import { Box, TextField, Tooltip } from '@mui/material';
import get from 'lodash/get';
import { calcChargeHours } from 'resources/rate_settings/rateSettingsCalculations';
import { Info } from '@mui/icons-material';
import { useRecordContext } from 'react-admin';

const DEFAULT_SHIFT_LENGTH = 12;

export const Market12HourRateField = ({
  source,
  inheritedSettings,
  isHospital,
  locationTypeMarkup,
}: {
  source: string;
  inheritedSettings: any;
  isHospital: boolean;
  locationTypeMarkup: number;
}) => {
  const record = useRecordContext();
  const recordBaseChargeRateCents = get(record, `${source}.base_charge_rate_cents`);
  const hasRecordBaseChargeRate = !!recordBaseChargeRateCents;

  if (
    !inheritedSettings ||
    Object.keys(inheritedSettings).length === 0 ||
    hasRecordBaseChargeRate
  ) {
    return null;
  }

  const {
    base_charge_rate_cents,
    use_blended_rate,
    blended_rates,
    non_blended_rates,
    invoicing_markup,
    misc_markup,
    operational_markup,
  } = inheritedSettings;

  const settingsToUse = use_blended_rate ? blended_rates : non_blended_rates;

  const totalChargeHours = calcChargeHours({
    hours: DEFAULT_SHIFT_LENGTH,
    charge_max_daily_regular_hours: settingsToUse?.charge_max_daily_regular_hours,
    charge_max_daily_ot_hours: settingsToUse?.charge_max_daily_overtime_hours,
    charge_overtime_multiplier: settingsToUse?.charge_overtime_multiplier,
    charge_double_overtime_multiplier: settingsToUse?.charge_double_overtime_multiplier,
  });

  const blendedRateFactor = totalChargeHours / DEFAULT_SHIFT_LENGTH || 1;
  const locationTypeModifier = 1 + (locationTypeMarkup || 0);
  const compoundingMarkups =
    (1 + (invoicing_markup || 0)) * (1 + (misc_markup || 0)) * (1 + (operational_markup || 0));
  const market12HourRate = Math.round(
    base_charge_rate_cents * locationTypeModifier * blendedRateFactor * compoundingMarkups,
  );

  const roundedMarket12HourRate = isHospital
    ? market12HourRate + ((5 - (market12HourRate % 5)) % 5)
    : market12HourRate;

  const formattedValue = Number.isFinite(roundedMarket12HourRate)
    ? (roundedMarket12HourRate / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
      })
    : '';

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Tooltip
        title={
          <div style={{ margin: '4px', fontSize: '0.8rem', lineHeight: 1.4 }}>
            This rate is a snapshot based on the current market rate for a 12-hour shift with
            applicable location level markups and modifiers.
          </div>
        }
        arrow
        placement="right"
      >
        <TextField
          label={
            <Box display="flex" alignItems="center" gap={0.5}>
              <span>Market 12hr Charge Rate</span>
              <Info
                style={{
                  fontSize: '16px',
                  cursor: 'default',
                }}
              />
            </Box>
          }
          value={formattedValue}
          fullWidth
          disabled
        />
      </Tooltip>
    </Box>
  );
};
